import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, unref as _unref, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, withKeys as _withKeys } from "vue"

const _hoisted_1 = { key: 1 }
const _hoisted_2 = { key: 0 }
const _hoisted_3 = { class: "p-4 bg-primary text-white flex justify-between items-center" }
const _hoisted_4 = { class: "text-lg font-semibold" }
const _hoisted_5 = {
  key: 0,
  class: "text-sm opacity-80"
}
const _hoisted_6 = { class: "flex space-x-2" }
const _hoisted_7 = ["onClick"]
const _hoisted_8 = ["onClick"]
const _hoisted_9 = { class: "p-4 bg-white" }
const _hoisted_10 = { class: "w-full border-collapse" }
const _hoisted_11 = { class: "py-3 px-2 font-medium" }
const _hoisted_12 = {
  key: 0,
  class: "text-red-500 ml-1"
}
const _hoisted_13 = { class: "py-3 px-2 text-right" }
const _hoisted_14 = { class: "flex items-center justify-end" }
const _hoisted_15 = { class: "mr-2" }
const _hoisted_16 = {
  key: 0,
  class: "text-gray-500 text-sm"
}
const _hoisted_17 = {
  key: 1,
  class: "ml-2"
}
const _hoisted_18 = {
  key: 0,
  class: "border-b"
}
const _hoisted_19 = { class: "py-3 px-2 text-right" }
const _hoisted_20 = { class: "flex items-center justify-end" }
const _hoisted_21 = ["value", "onInput", "onBlur", "onKeyup", "disabled"]
const _hoisted_22 = {
  key: 0,
  class: "ml-2"
}
const _hoisted_23 = {
  key: 1,
  class: "p-4 text-center bg-gray-50 border rounded"
}

import { ref, watch, onMounted } from "vue";
import { useEquipment } from "@/composables/tasks/useEquipment";
import { useEquipmentForm } from "@/composables/tasks/useEquipmentForm";
import EquipmentConfigForm from "@/components/v2/Tasks/Misc/Equipment/Forms/EquipmentConfigForm.vue";
import { EquipmentConfiguration } from "@/types/v2";

// ===================================================================
// State
// ===================================================================

export default /*@__PURE__*/_defineComponent({
  __name: 'EquipmentConfigurations',
  setup(__props) {

const showEditForm = ref(false);
const editingConfigId = ref<string | number | null>(null);

// ===================================================================
// Composables
// ===================================================================
// Equipment operations
const {
  getEquipmentConfigurations,
  formatValue,
  getOptionLabel,
  removeEquipmentConfiguration,
  hasWeightIdRequirement,
  getWeightEquipmentConfig,
  getWeightId,
  updateConfigValue,
} = useEquipment();

// Helper to convert snake_case to camelCase
const toCamelCase = (str: string): string => {
  return str.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
};

// Get value from config.values, handling both snake_case and camelCase keys
const getConfigValue = (config: EquipmentConfiguration, optionName: string): any => {
  if (!config.values) return undefined;
  
  // Try camelCase first (since API converts to camelCase)
  const camelKey = toCamelCase(optionName);
  if (camelKey in config.values) {
    return config.values[camelKey];
  }
  
  // Fallback to original snake_case
  if (optionName in config.values) {
    return config.values[optionName];
  }
  
  return undefined;
};

// Equipment form
const {
  initEditForm,
  cancelForm
} = useEquipmentForm();

// ===================================================================
// Actions
// ===================================================================
// Start editing a configuration
const startEditing = (config: EquipmentConfiguration) => {
  editingConfigId.value = config.id;
  initEditForm(config);
  showEditForm.value = true;
};

// Handle form cancellation
const handleCancel = () => {
  cancelForm();
  showEditForm.value = false;
  editingConfigId.value = null;
};

// Handle successful save
const handleSaved = () => {
  showEditForm.value = false;
  editingConfigId.value = null;
};

// Weight ID state and handlers
const weightId = ref<Record<string, string>>({});
const isUpdatingWeightId = ref<Record<string, boolean>>({});

// Get weight ID for a specific config
const getWeightIdForConfig = (config: EquipmentConfiguration): string => {
  if (config.equipmentType.name !== 'Weight') return '';
  if (!config.values || !config.values.weightIdRequired) return '';
  
  // Initialize weightId if it doesn't exist
  if (!('weightId' in config.values)) {
    config.values.weightId = '';
  }
  
  return (config.values.weightId as string) || '';
};

// Initialize weight ID for all Weight configs
const initializeWeightIds = () => {
  getEquipmentConfigurations.value.forEach(config => {
    if (config.equipmentType.name === 'Weight' && config.values?.weightIdRequired) {
      const configId = config.id.toString();
      weightId.value[configId] = getWeightIdForConfig(config);
    }
  });
};

// Update weight ID
const handleWeightIdChange = async (config: EquipmentConfiguration, newValue: string) => {
  if (!config || config.equipmentType.name !== 'Weight') return;

  const configId = config.id.toString();
  isUpdatingWeightId.value[configId] = true;
  
  try {
    await updateConfigValue(config, 'weightId', newValue);
    weightId.value[configId] = newValue;
  } catch (error) {
    console.error('Error updating weight ID:', error);
  } finally {
    isUpdatingWeightId.value[configId] = false;
  }
};

// Watch for weight config changes
onMounted(() => {
  initializeWeightIds();
});

watch(() => getEquipmentConfigurations.value, () => {
  initializeWeightIds();
}, { deep: true, immediate: true });

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", null, [
    (showEditForm.value)
      ? (_openBlock(), _createBlock(EquipmentConfigForm, {
          key: 0,
          onCancel: handleCancel,
          onSaved: handleSaved
        }))
      : (_openBlock(), _createElementBlock("div", _hoisted_1, [
          (_unref(getEquipmentConfigurations) && _unref(getEquipmentConfigurations).length > 0)
            ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(getEquipmentConfigurations), (config) => {
                  return (_openBlock(), _createElementBlock("div", {
                    key: config.id,
                    class: "mb-6 border rounded overflow-hidden"
                  }, [
                    _createElementVNode("div", _hoisted_3, [
                      _createElementVNode("div", null, [
                        _createElementVNode("h3", _hoisted_4, _toDisplayString(config.equipmentType.name), 1),
                        (config.equipmentType.description)
                          ? (_openBlock(), _createElementBlock("p", _hoisted_5, _toDisplayString(config.equipmentType.description), 1))
                          : _createCommentVNode("", true)
                      ]),
                      _createElementVNode("div", _hoisted_6, [
                        _createElementVNode("button", {
                          onClick: ($event: any) => (startEditing(config)),
                          class: "bg-white text-primary px-3 py-1 rounded text-sm"
                        }, " Edit ", 8, _hoisted_7),
                        _createElementVNode("button", {
                          onClick: ($event: any) => (_unref(removeEquipmentConfiguration)(config)),
                          class: "bg-white text-red-500 hover:bg-red-100 px-3 py-1 rounded text-sm"
                        }, " Remove ", 8, _hoisted_8)
                      ])
                    ]),
                    _createElementVNode("div", _hoisted_9, [
                      _createElementVNode("table", _hoisted_10, [
                        _createElementVNode("tbody", null, [
                          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(config.equipmentType.configOptions, (option) => {
                            return (_openBlock(), _createElementBlock("tr", {
                              key: option.name,
                              class: "border-b"
                            }, [
                              _createElementVNode("td", _hoisted_11, [
                                _createTextVNode(_toDisplayString(_unref(getOptionLabel)(config.equipmentType.configOptions, option.name)) + " ", 1),
                                (option.required)
                                  ? (_openBlock(), _createElementBlock("span", _hoisted_12, "*"))
                                  : _createCommentVNode("", true)
                              ]),
                              _createElementVNode("td", _hoisted_13, [
                                _createElementVNode("div", _hoisted_14, [
                                  _createElementVNode("span", _hoisted_15, _toDisplayString(_unref(formatValue)(getConfigValue(config, option.name), option.type)), 1),
                                  (option.unit)
                                    ? (_openBlock(), _createElementBlock("span", _hoisted_16, _toDisplayString(option.unit), 1))
                                    : _createCommentVNode("", true),
                                  (option.modifiable_in_app)
                                    ? (_openBlock(), _createElementBlock("span", _hoisted_17, _cache[0] || (_cache[0] = [
                                        _createElementVNode("span", { class: "inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800" }, " Modifiable ", -1)
                                      ])))
                                    : _createCommentVNode("", true)
                                ])
                              ])
                            ]))
                          }), 128)),
                          (config.equipmentType.name === 'Weight' && config.values?.weightIdRequired === true)
                            ? (_openBlock(), _createElementBlock("tr", _hoisted_18, [
                                _cache[2] || (_cache[2] = _createElementVNode("td", { class: "py-3 px-2 font-medium" }, [
                                  _createTextVNode(" Weight ID "),
                                  _createElementVNode("span", { class: "text-red-500 ml-1" }, "*")
                                ], -1)),
                                _createElementVNode("td", _hoisted_19, [
                                  _createElementVNode("div", _hoisted_20, [
                                    _createElementVNode("input", {
                                      type: "text",
                                      value: weightId.value[config.id.toString()] || '',
                                      onInput: ($event: any) => (weightId.value[config.id.toString()] = ($event.target as HTMLInputElement).value),
                                      onBlur: ($event: any) => (handleWeightIdChange(config, weightId.value[config.id.toString()] || '')),
                                      onKeyup: _withKeys(($event: any) => (handleWeightIdChange(config, weightId.value[config.id.toString()] || '')), ["enter"]),
                                      disabled: isUpdatingWeightId.value[config.id.toString()],
                                      class: "border border-gray-300 rounded px-3 py-1 text-right focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed",
                                      placeholder: "Enter Weight ID"
                                    }, null, 40, _hoisted_21),
                                    (isUpdatingWeightId.value[config.id.toString()])
                                      ? (_openBlock(), _createElementBlock("span", _hoisted_22, _cache[1] || (_cache[1] = [
                                          _createElementVNode("svg", {
                                            class: "animate-spin h-4 w-4 text-gray-500",
                                            xmlns: "http://www.w3.org/2000/svg",
                                            fill: "none",
                                            viewBox: "0 0 24 24"
                                          }, [
                                            _createElementVNode("circle", {
                                              class: "opacity-25",
                                              cx: "12",
                                              cy: "12",
                                              r: "10",
                                              stroke: "currentColor",
                                              "stroke-width": "4"
                                            }),
                                            _createElementVNode("path", {
                                              class: "opacity-75",
                                              fill: "currentColor",
                                              d: "M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                            })
                                          ], -1)
                                        ])))
                                      : _createCommentVNode("", true)
                                  ])
                                ])
                              ]))
                            : _createCommentVNode("", true)
                        ])
                      ])
                    ])
                  ]))
                }), 128))
              ]))
            : (_openBlock(), _createElementBlock("div", _hoisted_23, _cache[3] || (_cache[3] = [
                _createElementVNode("p", { class: "text-gray-500" }, "No equipment configurations have been set up for this task.", -1)
              ])))
        ]))
  ]))
}
}

})